import React from 'react';
import { UserMessage } from './UserMessage';
import { AssistantMessage } from './AssistantMessage';
import { ToolCall } from './ToolCall';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: Array<
    | { type: 'text'; text: string }
    | { type: 'tool-call'; toolCallId: string; toolName: string; args: any; argsText: string; result?: string; status?: any }
  >;
  status?: { type: 'incomplete'; reason: 'error'; error: Error };
}

interface MessageRowProps {
  message: Message;
}

export const MessageRow = React.memo<MessageRowProps>(({ message }) => {
  if (message.role === 'user') {
    const textContent = message.content
      .filter((c) => c.type === 'text')
      .map((c) => (c as { type: 'text'; text: string }).text)
      .join(' ');
    return <UserMessage content={textContent} />;
  }

  return (
    <div className="space-y-8">
      {message.content.map((part, idx) => {
        if (part.type === 'text') {
          const textContent = (part as { type: 'text'; text: string }).text;
          if (textContent.trim()) {
            return <AssistantMessage key={`${message.id}-part-${idx}`} content={textContent} />;
          }
          return null;
        } else if (part.type === 'tool-call') {
          const toolCall = part as {
            type: 'tool-call';
            toolCallId: string;
            toolName: string;
            args: any;
            argsText: string;
            result?: string;
            status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
          };

          let status = toolCall.status;
          if (!status) {
            status = { type: 'running' as const };
          }

          return (
            <ToolCall
              key={toolCall.toolCallId}
              toolName={toolCall.toolName}
              toolCallId={toolCall.toolCallId}
              args={toolCall.args}
              argsText={toolCall.argsText}
              result={toolCall.result}
              status={status}
            />
          );
        }
        return null;
      })}

      {message.status?.type === 'incomplete' && message.status.reason === 'error' && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {message.status.error?.message || 'An error occurred'}
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return prev.message === next.message;
});
MessageRow.displayName = 'MessageRow';
